import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/browse", label: "Browse" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dashPath =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "shop_owner"
      ? "/owner"
      : "/dashboard";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,box-shadow] duration-500 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-[0_8px_30px_rgba(42,36,30,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <img
            src="/logo.png"
            alt="NearbyDeals"
            className="h-12 w-auto group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-primary ${
                location.pathname === l.to ? "text-primary" : "text-foreground/80"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="flex items-center gap-2 rounded-full bg-secondary pl-1 pr-4 py-1 border border-border hover:border-primary/40 transition-colors"
                >
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold uppercase">
                    {user.name?.[0] || "U"}
                  </span>
                  <span className="text-sm font-semibold max-w-[120px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-2">
                  {user.role === "admin" && <ShieldCheck className="w-4 h-4 text-primary" />}
                  <span className="capitalize">{user.role?.replace("_", " ")}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-dashboard-link" onClick={() => navigate(dashPath)}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-logout"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link
                to="/auth"
                data-testid="nav-login"
                className="text-sm font-semibold hover:text-primary transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/auth?mode=register"
                data-testid="nav-signup"
                className="rounded-full bg-primary text-primary-foreground text-sm font-semibold px-5 py-2.5 hover:bg-primary/90 transition-colors"
              >
                Get started
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden grid place-items-center w-10 h-10 rounded-full bg-secondary border border-border"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border px-5 py-6 space-y-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-base font-semibold"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-border flex flex-col gap-3">
            {user ? (
              <>
                <Link to={dashPath} onClick={() => setOpen(false)} className="font-semibold">
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="text-left font-semibold text-primary"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/auth" onClick={() => setOpen(false)} className="font-semibold">
                  Log in
                </Link>
                <Link
                  to="/auth?mode=register"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-primary text-primary-foreground font-semibold px-5 py-3 text-center"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
